import { Badge, LegacyStack, Text, TextContainer } from '@shopify/polaris'
import { Tone } from '@shopify/polaris/build/ts/src/components/Badge'
import React from 'react'

import {
  CommaList,
  formatMoney,
  formatOrdinal,
  fragmentList,
  gidToId,
  pluralize,
} from '../common/helpers'
import { SellingPlanInterval } from '../common/plan-models'
import { useShopCurrency } from '../common/Price'
import { WorkflowGroup } from '../common/workflow-group-models'
import {
  actionIsSupportedByTriggers,
  formatWorkflowActionPrice,
  WorkflowAction,
  workflowActionLabels,
  WorkflowAdjustLinePriceForPricingPolicyDataType,
  WorkflowFilter,
  workflowFilterLabels,
  WorkflowFilterType,
  WorkflowProductDataType,
  WorkflowRootFilterType,
  WorkflowTagsDataType,
  workflowTriggerLabels,
  WorkflowTriggerType,
} from '../common/workflow-models'
import { useSellingPlanNames } from '../selling-plans-index-page/api'
import { subscriptionFlowWorkflowAfterOrderNumber } from '../subscription-flow-page/api'
import { PaymentNumberFilterParams } from './PaymentNumberFilter'
import { operatorLabels, ProductFieldFilterParams } from './ProductFieldFilter'

const ParamsList = ({ children }: { children: React.ReactNode[] }) => (
  <>
    <> (</>
    <CommaList>
      {children.map((node, index) => (
        <Text as="span" tone="subdued" key={index}>
          {node}
        </Text>
      ))}
    </CommaList>
    <>)</>
  </>
)

const TriggersLine = ({ triggers }: { triggers: WorkflowTriggerType[] }) => {
  let items: React.ReactNode[] = []

  triggers.forEach((trigger, index) => {
    items.push(index === 0 ? <b>When </b> : <b> or </b>)
    items.push(workflowTriggerLabels[trigger])
  })

  return <p>{fragmentList(items)}</p>
}

const filterLineItems = (
  [type, params]: WorkflowFilter,
  spNames: Record<string, string>
): React.ReactNode[] => {
  const hasSpNames = Object.keys(spNames).length > 0
  let items: React.ReactNode[] = []

  const typesWithProductTitles: WorkflowFilterType[] = [
    'subscription-contains-all-selected-product-variants',
    'subscription-contains-any-of-selected-product-variants',
    'subscription-does-not-contain-any-of-selected-product-variants',
    'subscription-contains-all-selected-products',
    'subscription-contains-any-of-selected-products',
    'subscription-does-not-contain-any-of-selected-products',
  ]

  const descriptionLabels = {
    ...workflowFilterLabels,
    'subscription-contains-any-matching-product-title': 'Subscription contains ANY product where',
    'subscription-contains-any-matching-product-variant-title':
      'Subscription contains ANY product where',
    'after-nth-order': '',
  }

  items.push(descriptionLabels[type])

  if (type === 'after-nth-order') {
    const filterParams = params as PaymentNumberFilterParams
    items.push(`Subscription has `)
    items.push(<b>{filterParams[0]}</b>)
    items.push(` ${pluralize(parseInt(filterParams[0]), 'order')}`)
  }

  if (typesWithProductTitles.includes(type)) {
    items.push(<ParamsList>{params.map((p) => p[1])}</ParamsList>)
  }

  if (type === 'subscription-contains-any-matching-product-title') {
    const filterParams = params as ProductFieldFilterParams
    items.push(` title ${operatorLabels[filterParams[0]]} '${filterParams[1]}'`)
  }

  if (type === 'subscription-contains-any-matching-product-variant-title') {
    const filterParams = params as ProductFieldFilterParams
    items.push(` variant title ${operatorLabels[filterParams[0]]} '${filterParams[1]}'`)
  }

  if (hasSpNames && type === 'subscription-contains-any-of-selected-selling-plans') {
    items.push(
      <ParamsList>{params.map((gid) => spNames[gid] || `#${gidToId(gid)} (deleted)`)}</ParamsList>
    )
  }

  if (hasSpNames && type === 'subscription-contains-any-of-selected-selling-plan-groups') {
    items.push(
      <ParamsList>{params.map((gid) => spNames[gid] || `#${gidToId(gid)} (deleted)`)}</ParamsList>
    )
  }

  return fragmentList(items)
}

export const FiltersLine = ({
  rootFilterType,
  filters,
  prefix,
}: {
  rootFilterType: WorkflowRootFilterType
  filters: WorkflowFilter[]
  prefix?: string
}) => {
  const spNames = useSellingPlanNames()

  let items: React.ReactNode[] = []
  const conjunction = () => (rootFilterType === 'and' ? <b> and </b> : <b> or </b>)

  filters.forEach((filter, index) => {
    items.push(index === 0 ? <b>{prefix || 'If'} </b> : conjunction())
    items = [...items, ...filterLineItems(filter, spNames)]
  })

  return <p>{fragmentList(items)}</p>
}

export const AppliesToOrdersLine = ({ workflowGroup }: { workflowGroup: WorkflowGroup }) => {
  const orderNumbers = workflowGroup.workflows.map((workflow) =>
    subscriptionFlowWorkflowAfterOrderNumber(workflow)
  )

  if (orderNumbers.length === 0) {
    return (
      <Text as="span" tone="subdued">
        No orders specified
      </Text>
    )
  }

  return (
    <p>
      <Text as="span" fontWeight="semibold">
        Applies to orders:
      </Text>{' '}
      {orderNumbers.map((orderNumber) => (
        <>
          <Badge key={orderNumber} tone="success">
            {formatOrdinal(orderNumber + 1)}
          </Badge>{' '}
        </>
      ))}
    </p>
  )
}

export const ProductBadge = ({
  productData,
  tone,
}: {
  productData: WorkflowProductDataType
  tone?: Tone
}) => {
  if (productData.length > 0) {
    return <Badge tone={tone}>{productData[1]}</Badge>
  }

  return <Badge tone="new">No variant selected</Badge>
}

const ActionLine = ({ action }: { action: WorkflowAction }) => {
  const currencyCode = useShopCurrency()

  let items: React.ReactNode[] = []
  items.push(<b>{workflowActionLabels[action[0]]}</b>)
  items.push(<b>:</b>)

  if (
    ['add-customer-tags', 'remove-customer-tags', 'add-order-tags', 'remove-order-tags'].includes(
      action[0]
    )
  ) {
    const tags = action[1] as WorkflowTagsDataType
    tags.forEach((param, index) => {
      items.push(
        <React.Fragment key={`tag-${index}`}>
          {' '}
          <Badge tone={action[0].startsWith('remove-') ? undefined : 'success'}>{param}</Badge>
        </React.Fragment>
      )
    })
    tags.length === 0 &&
      items.push(
        <>
          {' '}
          <Text as="span" tone="subdued">
            No tags selected
          </Text>
        </>
      )
  }

  if (
    ['add-one-time-gift-product', 'remove-product', 'remove-product-variant'].includes(action[0]) &&
    action[1].length
  ) {
    const params = action[1] as WorkflowProductDataType
    items.push(
      <>
        {' '}
        <ProductBadge productData={params} tone="success" />
      </>
    )
  }

  if (action[0] === 'add-product' && action[1].length) {
    const [productData, quantity, currentPrice, recurringCycleLimit] = action[1]
    const formattedPrice = formatWorkflowActionPrice(currentPrice, currencyCode)
    items.push(
      <>
        {' '}
        <ProductBadge productData={productData} tone="success" /> x <b>{quantity}</b>
        {formattedPrice && (
          <>
            {' '}
            at <b>{formattedPrice}</b> per unit
          </>
        )}
        , that applies to <b>{recurringCycleLimit ?? 'all upcoming'}</b>{' '}
        {recurringCycleLimit ? pluralize(recurringCycleLimit, 'order') : 'orders'}
      </>
    )
  }

  if (action[0] === 'swap-product' && action[1].length) {
    const [sourceProductData, targetProductData, quantity, currentPrice] = action[1]
    const formattedPrice = formatWorkflowActionPrice(currentPrice, currencyCode)
    items.push(
      <>
        {' '}
        <ProductBadge productData={sourceProductData} tone={undefined} />
        <b>{' with '}</b>
        <ProductBadge productData={targetProductData} tone="success" />
        {quantity && (
          <>
            {' '}
            x <b>{quantity}</b>
          </>
        )}
        {formattedPrice && (
          <>
            {' '}
            at <b>{formattedPrice}</b> per unit
          </>
        )}
      </>
    )
  }
  if (action[0] === 'add-discount') {
    const [discountCodeName, recurringCycleLimit, discountType, discountValue, appliesOnEachItem] =
      action[1]
    const formattedValue =
      discountType === 'percentage'
        ? discountValue !== '' && `${discountValue}%`
        : formatWorkflowActionPrice(discountValue, currencyCode)

    items.push(
      <>
        {' '}
        {discountCodeName && <Badge tone="success">{discountCodeName}</Badge>}
        {!discountCodeName && <Badge tone="new">No discount name</Badge>}
        <span>
          {' '}
          {formattedValue && (
            <>
              at <b>{formattedValue}</b>{' '}
            </>
          )}
          that applies to
          {discountType === 'fixedAmount' && appliesOnEachItem === 1 ? ' each item and ' : ' '}
          <b>{recurringCycleLimit ? recurringCycleLimit : 'all upcoming'}</b>{' '}
          {recurringCycleLimit ? pluralize(parseInt(recurringCycleLimit), 'order') : 'orders'}
        </span>
      </>
    )
  }

  if (action[0] === 'edit-delivery-frequency') {
    const [intervalCount, intervalUnit, prePaidDeliveries] = action[1]
    const displayIntervalUnit = SellingPlanInterval[intervalUnit]

    items.push(
      <>
        {' '}
        Delivery every <Badge tone="success">{`${intervalCount}`}</Badge>{' '}
        <Badge tone="success">{`${displayIntervalUnit}`}</Badge>
        {prePaidDeliveries > 1 ? (
          <>
            {' '}
            with <Badge tone="success">{`${prePaidDeliveries} prepaid deliveries`}</Badge>
          </>
        ) : (
          <>
            {' '}
            <Text as="span" tone="subdued">
              (regular subscription, no prepaid deliveries)
            </Text>
          </>
        )}
      </>
    )
  }

  if (action[0] === 'edit-delivery-method') {
    const [deliveryMethodTitle, deliveryMethodCode, deliveryPrice] = action[1]

    const title = deliveryMethodTitle ? (
      <>Title: {deliveryMethodTitle && <Badge tone="success">{deliveryMethodTitle}</Badge>}</>
    ) : null

    const code = deliveryMethodCode ? (
      <>Code: {deliveryMethodCode && <Badge tone="success">{deliveryMethodCode}</Badge>}</>
    ) : null

    const formattedPrice = formatWorkflowActionPrice(deliveryPrice, currencyCode)
    const price = formattedPrice ? (
      <>
        Price: <b>{formattedPrice}</b>
      </>
    ) : null

    items.push(
      <>
        {[title, code, price]
          .filter(Boolean)
          .reduce(
            (acc, item) => [...acc, acc.length > 0 ? ', ' : ' ', item],
            [] as React.ReactNode[]
          )}
      </>
    )
  }

  if (action[0] === 'adjust-line-price-for-pricing-policy') {
    const lineItems = action[1] as WorkflowAdjustLinePriceForPricingPolicyDataType
    lineItems.forEach((item, index) => {
      items.push(
        <React.Fragment key={`pricing-policy-${index}`}>
          {' '}
          <Badge tone="success">{item[1]}</Badge> to{' '}
          <b>{formatMoney(parseFloat(item[2]) * 100, currencyCode)}</b>
        </React.Fragment>
      )
    })
    if (lineItems.length === 0) {
      items.push(
        <>
          {' '}
          <Text as="span" tone="subdued">
            No line items to adjust
          </Text>
        </>
      )
    }
  }

  return <TextContainer spacing="tight">{fragmentList(items)}</TextContainer>
}

export const isValidAction = (action: WorkflowAction): boolean => {
  if (
    [
      'add-discount',
      'remove-one-time-gift-products',
      'remove-expired-discount',
      'adjust-line-price-for-pricing-policy',
    ].includes(action[0])
  ) {
    return true
  }

  if (
    action[0] === 'swap-product' &&
    action[1].length >= 2 &&
    action[1][0]?.length === 2 &&
    action[1][1]?.length === 2
  ) {
    return true
  }

  if (
    [
      'add-one-time-gift-product',
      'remove-product',
      'remove-product-variant',
      'add-customer-tags',
      'remove-customer-tags',
      'add-order-tags',
    ].includes(action[0]) &&
    action[1].length > 0
  ) {
    return true
  }

  return false
}

export const WorkflowDescriptionTriggers = ({ triggers }: { triggers: WorkflowTriggerType[] }) => (
  <>
    {triggers.length > 0 && <TriggersLine triggers={triggers} />}
    {triggers.length === 0 && (
      <Text as="span" tone="subdued">
        No events selected
      </Text>
    )}
  </>
)

export const WorkflowDescriptionActions = ({
  actions,
  triggers,
  tightSpacing,
}: {
  actions: WorkflowAction[]
  triggers: WorkflowTriggerType[]
  tightSpacing?: boolean
}) => {
  const filteredActions = actions.filter((action) =>
    actionIsSupportedByTriggers(action[0], triggers)
  )

  return (
    <LegacyStack spacing={tightSpacing ? 'extraTight' : 'baseTight'} vertical>
      {filteredActions.map((action, index) => (
        <ActionLine action={action} key={`action-line-${index}`} />
      ))}
      {actions.length === 0 && (
        <Text as="span" tone="subdued">
          No action specified
        </Text>
      )}
    </LegacyStack>
  )
}

export const WorkflowDescription = ({
  triggers,
  filters,
  rootFilterType,
  actions,
  tightSpacing,
}: {
  triggers: WorkflowTriggerType[]
  filters: WorkflowFilter[]
  rootFilterType: WorkflowRootFilterType
  actions: WorkflowAction[]
  tightSpacing?: boolean
}) => (
  <LegacyStack vertical spacing={tightSpacing ? 'extraTight' : 'baseTight'}>
    <WorkflowDescriptionTriggers triggers={triggers} />
    {filters.length > 0 && <FiltersLine rootFilterType={rootFilterType} filters={filters} />}
    <WorkflowDescriptionActions actions={actions} triggers={triggers} tightSpacing={tightSpacing} />
  </LegacyStack>
)
